const VIDEO_EXTENSIONS = new Set([".mp4", ".webm", ".mov", ".ogg"]);

// cspell:ignore youtu
const YOUTUBE_HOSTNAMES = new Set(["www.youtube.com", "youtube.com", "youtu.be"]);

const YOUTUBE_VIDEO_ID_PATTERN = /^[\w-]{11}$/;

const VIMEO_HOSTNAMES = new Set(["vimeo.com", "www.vimeo.com", "player.vimeo.com"]);

export type GuideMediaType = "image" | "video" | "youtube" | "vimeo";

function tryParseUrl(url: string): URL | undefined {
  try {
    return new URL(url);
  } catch {
    return undefined;
  }
}

export function getGuideMediaType(url: string): GuideMediaType {
  const parsed = tryParseUrl(url);
  if (!parsed) {
    return "image";
  }

  const extension = parsed.pathname.slice(parsed.pathname.lastIndexOf(".")).toLowerCase();
  if (VIDEO_EXTENSIONS.has(extension)) {
    return "video";
  }

  if (YOUTUBE_HOSTNAMES.has(parsed.hostname)) {
    return "youtube";
  }

  if (VIMEO_HOSTNAMES.has(parsed.hostname)) {
    return "vimeo";
  }

  return "image";
}

export function getYouTubeEmbedUrl(url: string): string {
  const parsed = tryParseUrl(url);
  if (!parsed || !YOUTUBE_HOSTNAMES.has(parsed.hostname)) {
    return url;
  }

  if (parsed.hostname === "youtu.be") {
    const videoId = parsed.pathname.slice(1);
    return YOUTUBE_VIDEO_ID_PATTERN.test(videoId)
      ? `https://www.youtube.com/embed/${videoId}`
      : url;
  }

  const watchId = parsed.searchParams.get("v");
  if (watchId && YOUTUBE_VIDEO_ID_PATTERN.test(watchId)) {
    return `https://www.youtube.com/embed/${watchId}`;
  }

  if (parsed.pathname.startsWith("/embed/")) {
    return url;
  }

  return url;
}

export function getVimeoEmbedUrl(url: string): string {
  const parsed = tryParseUrl(url);
  if (!parsed || !VIMEO_HOSTNAMES.has(parsed.hostname)) {
    return url;
  }

  if (parsed.hostname === "player.vimeo.com" && parsed.pathname.startsWith("/video/")) {
    return url;
  }

  const videoId = /^\/(\d+)/.exec(parsed.pathname);
  if (videoId?.[1]) {
    return `https://player.vimeo.com/video/${videoId[1]}`;
  }

  return url;
}
